import { useEffect, useMemo, useSyncExternalStore } from 'react';
import config from '../../../config.json';
import { resolveForceRotateDeg, shouldUseTiltSignal } from './forceRotate';

/**
 * Shared constant for landscape detection threshold
 * Devices with height < this value (in landscape) are considered to have limited vertical space
 */
export const LANDSCAPE_HEIGHT_THRESHOLD = 600;

/**
 * Detect if device is a touch-based mobile device
 */
const isTouchDevice = () => {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

/**
 * Detect if device should be treated as mobile
 * Includes phones and tablets with touch capability
 */
const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;

  const userAgent = navigator.userAgent.toLowerCase();
  const isTouch = isTouchDevice();

  // Check for mobile user agents
  const isMobileUA = /iphone|ipod|android|blackberry|windows phone/g.test(userAgent);

  // Check for tablets specifically
  const isTablet = /(ipad|tablet|playbook|silk)|(android(?!.*mobile))/g.test(userAgent);

  // Check for small screens
  const maxWidth = window.matchMedia('(max-width: 768px)').matches;

  // Return true if it's a touch device AND (mobile phone, tablet, or small screen)
  return isTouch && (isMobileUA || isTablet || maxWidth);
};

// ---------------------------------------------------------------------------
// Single atomic source of truth for orientation
//
// Why this exists: prior versions of these hooks each ran their own
// `addEventListener('resize', ...)` and read `window.inner*` independently.
// On iOS Safari those reads can briefly disagree across React subscribers
// (e.g., during address-bar collapse, fullscreen transitions, or rotation
// animations), which produced a 180° render bug because two `rotate(90deg)`
// transforms — one in the player, one in the gamepad — could both be applied
// in the same frame.
//
// `useOrientationState` is the new shared store: every consumer subscribes
// to the SAME matchMedia/screen.orientation events via `useSyncExternalStore`,
// so React commits all of them in the same render pass with the same value.
// The actual `transform: rotate(...)` lives in CSS gated by
// `@media (orientation: portrait)` (see styles.css), so the rotation is
// applied/removed by the browser atomically with viewport updates.
// ---------------------------------------------------------------------------

export interface OrientationSnapshot {
  /** True when the viewport is taller than wide (matchMedia('(orientation: portrait)')). */
  isViewportPortrait: boolean;
  /** Physical screen rotation in degrees (0, 90, 180, 270); 0 when unsupported. */
  angle: number;
  /** Live viewport width — included so consumers don't read window mid-render. */
  innerWidth: number;
  /** Live viewport height — drives the `LANDSCAPE_HEIGHT_THRESHOLD` check. */
  innerHeight: number;
  /**
   * Resolved force-landscape rotation (90 or -90). Single source of truth for
   * both the `--force-rotate-angle` CSS var and the touch un-rotation. Derived
   * from the accelerometer when available (see forceRotate.ts), so it flips
   * correctly for both landscape holds even when `angle` is pinned to 0.
   */
  forceRotateDeg: number;
}

const FORCE_ROTATE_CSS_VAR = '--force-rotate-angle';

// Latest accelerometer left-right tilt; null until a `deviceorientation` event
// arrives (or forever on desktop / iOS without motion permission).
let latestGamma: number | null = null;
// Last committed rotation, used as hysteresis input for the ambiguous near-flat
// gamma zone. Defaults to 90 to preserve legacy behavior before any signal.
let committedRotateDeg = 90;
// Last real landscape `screen.orientation.angle` (90 or 270) observed before
// `screen.orientation.lock('portrait')` pins the angle to 0 in fullscreen.
// While locked, gamma is re-expressed in the portrait frame and oscillates, so
// this stable pre-lock angle is what we hold instead. Reset on fullscreen exit
// so a later genuine portrait can't be mis-seeded.
let lastLandscapeAngle: number | null = null;

const EMPTY_SNAPSHOT: OrientationSnapshot = {
  isViewportPortrait: false,
  angle: 0,
  innerWidth: 0,
  innerHeight: 0,
  forceRotateDeg: 90,
};

const getOrientationSnapshot = (): OrientationSnapshot => {
  if (typeof window === 'undefined') return EMPTY_SNAPSHOT;
  // Sole signal: inner-dimension comparison. This is spec-equivalent to the
  // `(orientation: portrait)` media query (which the spec defines as
  // "width <= height") but, unlike `matchMedia`, the inner-dimension reads
  // settle synchronously with the React commit and never lag during the
  // initial-render / fullscreen-transition window. We intentionally do NOT
  // OR with matchMedia here: matchMedia can be stale in either direction
  // (we observed it stuck on landscape while the phone was portrait on real
  // Pixel 7 + Android 16 and Galaxy Note; the symmetric stuck-on-portrait
  // case on landscape rotate-back would produce the inverse bug under an
  // OR), and inner-dimensions are already authoritative for the actual
  // layout. We don't AND either, for the same reason. Confirmed elsewhere
  // in this codebase: the `RotateToLandscape` overlay in
  // `@playcastdotio/MobileGamepad` has used `innerHeight > innerWidth` as
  // its sole orientation read for the entire life of the project.
  //
  // Comparison is `>=` (not `>`) to match the CSS `(orientation: portrait)`
  // spec exactly: that media query treats `width == height` as portrait,
  // so we do too. The strict `>` form would classify a transient equal-
  // dimensions frame (e.g. mid-resize, mid-fullscreen) as landscape and
  // briefly disagree with the CSS rule, causing a one-frame flicker.
  const innerWidth = window.innerWidth;
  const innerHeight = window.innerHeight;
  const isViewportPortrait = innerHeight >= innerWidth;
  const screenOrientation = window.screen?.orientation;
  const angle = screenOrientation ? screenOrientation.angle ?? 0 : 0;
  const inFullscreen =
    typeof document !== 'undefined' && !!document.fullscreenElement;
  // Capture / release the real landscape angle so we can hold it once the
  // fullscreen portrait-lock pins `angle` back to 0.
  if (angle === 90 || angle === 270) {
    lastLandscapeAngle = angle;
  } else if ((angle === 0 || angle === 180) && !inFullscreen) {
    // Genuine portrait outside fullscreen: drop any previously captured angle so
    // a later fullscreen entry cannot reuse a stale (possibly opposite-hold)
    // value. Only a landscape angle seen immediately before the lock should
    // drive the locked rotation; otherwise we fall back to gamma.
    lastLandscapeAngle = null;
  }
  // Only trust the captured landscape angle while we are actually in the
  // fullscreen-locked state (angle forced to 0 while a landscape hold was seen).
  // Outside fullscreen, `angle` is authoritative on its own.
  const lockedLandscapeAngle =
    inFullscreen && angle === 0 ? lastLandscapeAngle : null;
  const forceRotateDeg = resolveForceRotateDeg(
    angle,
    latestGamma,
    committedRotateDeg,
    lockedLandscapeAngle,
  );
  return {
    isViewportPortrait,
    angle,
    innerWidth,
    innerHeight,
    forceRotateDeg,
  };
};

const writeRotateAngleVar = (deg: number) => {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty(FORCE_ROTATE_CSS_VAR, `${deg}deg`);
};

let cachedSnapshot: OrientationSnapshot = getOrientationSnapshot();
committedRotateDeg = cachedSnapshot.forceRotateDeg;

const refreshSnapshot = (): boolean => {
  const next = getOrientationSnapshot();
  const changed =
    next.isViewportPortrait !== cachedSnapshot.isViewportPortrait ||
    next.angle !== cachedSnapshot.angle ||
    next.innerWidth !== cachedSnapshot.innerWidth ||
    next.innerHeight !== cachedSnapshot.innerHeight ||
    next.forceRotateDeg !== cachedSnapshot.forceRotateDeg;
  if (changed) {
    const rotateChanged = next.forceRotateDeg !== cachedSnapshot.forceRotateDeg;
    cachedSnapshot = next;
    if (rotateChanged) {
      committedRotateDeg = next.forceRotateDeg;
      writeRotateAngleVar(next.forceRotateDeg);
    }
  }
  return changed;
};

// Initialize the CSS var before first paint so the @media rule renders correctly.
writeRotateAngleVar(cachedSnapshot.forceRotateDeg);

// ---------------------------------------------------------------------------
// Singleton subscriber pool
//
// `useSyncExternalStore` calls `subscribe` once per consumer mount. Naively
// adding 4 event listeners per call meant ~60 listeners across the app —
// each doing identical `matchMedia` reads on every iOS Safari `resize` (which
// fires on scroll / address-bar collapse / keyboard). Instead we keep a single
// `Set` of subscribers and attach exactly 4 listeners to the window the first
// time anyone subscribes; we detach when the set drains. Each event then does
// one `matchMedia` read, calls `refreshSnapshot`, and only fans out to React
// when the snapshot actually changed.
// ---------------------------------------------------------------------------

const subscribers = new Set<() => void>();
let detachListeners: (() => void) | null = null;

const sharedHandler = () => {
  if (!refreshSnapshot()) return;
  for (const cb of subscribers) cb();
};

const attachListeners = (): void => {
  if (detachListeners || typeof window === 'undefined') return;

  const portraitMq = window.matchMedia('(orientation: portrait)');
  const screenOrientation = window.screen?.orientation;

  if (typeof portraitMq.addEventListener === 'function') {
    portraitMq.addEventListener('change', sharedHandler);
  } else {
    // Safari < 14 fallback
    (portraitMq as unknown as { addListener: (cb: () => void) => void }).addListener(sharedHandler);
  }
  screenOrientation?.addEventListener?.('change', sharedHandler);
  window.addEventListener('orientationchange', sharedHandler);
  window.addEventListener('resize', sharedHandler);

  // Fullscreen enter/exit toggles whether the captured landscape angle applies,
  // so recompute the snapshot on the transition. The capture/release lifecycle
  // itself lives in getOrientationSnapshot (keyed off angle + fullscreen state).
  document.addEventListener('fullscreenchange', sharedHandler);

  // Physical tilt: the only signal for which way the phone is held when the
  // display is orientation-locked to portrait. Gated (via the unit-tested
  // shouldUseTiltSignal) to Android force-vertical only — iOS (auto-rotates,
  // works with the legacy heuristic) and desktop never attach this listener, so
  // their behavior is byte-for-byte unchanged. `refreshSnapshot` only fans out
  // when the resolved direction actually flips, so the ~60Hz event rate does
  // not cause render churn.
  const wantsTilt = shouldUseTiltSignal({
    forcePortrait: Boolean(config.FORCE_PORTRAIT_ORIENTATION),
    isMobile: isMobileDevice(),
    userAgent: navigator.userAgent,
    hasDeviceOrientationApi: typeof window.DeviceOrientationEvent !== 'undefined',
  });

  const tiltHandler = (event: DeviceOrientationEvent) => {
    // Ignore transient null readings (sensor warm-up / momentary dropouts) so a
    // single null cannot wipe a valid tilt and snap the resolver back to legacy
    // +90. A device that never reports keeps latestGamma at its initial null,
    // which correctly uses the legacy path.
    if (event.gamma === null) return;
    latestGamma = event.gamma;
    sharedHandler();
  };
  if (wantsTilt) {
    window.addEventListener('deviceorientation', tiltHandler);
  }

  detachListeners = () => {
    if (typeof portraitMq.removeEventListener === 'function') {
      portraitMq.removeEventListener('change', sharedHandler);
    } else {
      (portraitMq as unknown as { removeListener: (cb: () => void) => void }).removeListener(sharedHandler);
    }
    screenOrientation?.removeEventListener?.('change', sharedHandler);
    window.removeEventListener('orientationchange', sharedHandler);
    window.removeEventListener('resize', sharedHandler);
    document.removeEventListener('fullscreenchange', sharedHandler);
    if (wantsTilt) {
      window.removeEventListener('deviceorientation', tiltHandler);
      // Intentionally DO NOT reset latestGamma / committedRotateDeg here. They
      // stay consistent with the already-committed cachedSnapshot and the
      // --force-rotate-angle CSS var, so a re-subscription (e.g. a brief page
      // transition) resumes at the same rotation instead of flipping to the
      // legacy +90 on the first resize/orientationchange that fires before a
      // fresh deviceorientation reading. The retained gamma is a physical
      // measurement and thus the best estimate of the current hold; any real
      // change self-heals on the next deviceorientation event (~16ms).
    }
  };
  refreshSnapshot();
};

const subscribeOrientation = (cb: () => void): (() => void) => {
  if (typeof window === 'undefined') return () => undefined;
  subscribers.add(cb);
  attachListeners();
  return () => {
    subscribers.delete(cb);
    if (subscribers.size === 0 && detachListeners) {
      detachListeners();
      detachListeners = null;
    }
  };
};

const getCachedSnapshot = (): OrientationSnapshot => cachedSnapshot;

/**
 * Subscribe to the shared orientation store.
 * Always prefer this over reading `window.innerWidth/Height` directly so all
 * consumers stay in sync within a single React commit.
 */
export function useOrientationState(): OrientationSnapshot {
  return useSyncExternalStore(
    subscribeOrientation,
    getCachedSnapshot,
    getCachedSnapshot,
  );
}

// ---------------------------------------------------------------------------
// Legacy hook wrappers — preserve the existing public API.
// 15+ files in marketing-demo consume these; rewriting them as wrappers over
// `useOrientationState` keeps every call site behaving identically while
// switching the underlying source to the atomic store.
// ---------------------------------------------------------------------------

/**
 * Hook to detect if device is in landscape mode
 * On mobile devices, returns true when `FORCE_PORTRAIT_ORIENTATION` is set so
 * the player renders in landscape regardless of physical orientation.
 */
export function useIsLandscape(): boolean {
  const { isViewportPortrait, innerHeight } = useOrientationState();
  if (typeof window === 'undefined') return false;
  if (config.FORCE_PORTRAIT_ORIENTATION && isMobileDevice()) return true;
  return !isViewportPortrait && innerHeight < LANDSCAPE_HEIGHT_THRESHOLD;
}

/**
 * Hook to check if device is in portrait orientation (physically)
 */
export function useIsPortraitOrientation(): boolean {
  return useOrientationState().isViewportPortrait;
}

/**
 * Stable boolean: are we a force-landscape candidate at all?
 *
 * Independent of viewport orientation — the CSS @media (orientation: portrait)
 * rule decides whether to actually rotate at any given moment. Use this for the
 * `data-force-landscape` attribute on elements whose only purpose for that
 * attribute is to opt into the CSS-driven rotation; the value never needs to
 * change across orientation flips, so React is removed from the rotation
 * decision entirely.
 */
export function useShouldForceLandscape(): boolean {
  return useMemo(() => {
    if (typeof window === 'undefined') return false;
    return Boolean(config.FORCE_PORTRAIT_ORIENTATION) && isMobileDevice();
  }, []);
}

/**
 * Hook to check if page should force portrait mode
 * Used for CTA/exit/error/auth pages that should always display in portrait
 * ONLY applies on mobile devices when `FORCE_PORTRAIT_ORIENTATION` is enabled
 */
export function useForcePortrait(): {
  shouldRotate: boolean;
  isLandscapeOrientation: boolean;
} {
  const { isViewportPortrait } = useOrientationState();
  if (typeof window === 'undefined') {
    return { shouldRotate: false, isLandscapeOrientation: false };
  }
  const mobile = isMobileDevice();
  const isLandscapeOrientation = !isViewportPortrait;
  return {
    shouldRotate:
      config.FORCE_PORTRAIT_ORIENTATION && mobile && isLandscapeOrientation,
    isLandscapeOrientation,
  };
}

// ---------------------------------------------------------------------------
// Screen Orientation Lock helper
//
// Where supported (Android Chrome in fullscreen, iOS PWA 16.4+), this asks the
// OS to refuse to rotate the page entirely — bypassing the need for any
// client-side CSS rotation and making the 180° race impossible by physics.
// On iOS Safari (regular tab) the API rejects, and we fall back to the
// CSS @media (orientation: portrait) rule defined in styles.css.
//
// IMPORTANT: when `FORCE_PORTRAIT_ORIENTATION` is enabled, we *must not* lock
// to landscape on Android Chrome — doing so makes the OS physically rotate the
// display, which deactivates the `[data-force-landscape='true']` CSS rule
// (gated on `@media (orientation: portrait)`) and collapses the entire
// force-vertical UX. Instead we lock to *portrait* in that mode so Android
// Chrome stays put and the client-side rotation transform keeps working in
// fullscreen exactly as it does in the browser tab.
// ---------------------------------------------------------------------------

export type OrientationLockType =
  | 'any'
  | 'natural'
  | 'landscape'
  | 'portrait'
  | 'portrait-primary'
  | 'portrait-secondary'
  | 'landscape-primary'
  | 'landscape-secondary';

/**
 * Minimal, feature-detected view of `screen.orientation`. All members are
 * optional because support varies by platform (iOS Safari lacks `lock`); the
 * shape is shared with the rotation-test harness so the two definitions cannot
 * drift.
 */
export interface LockableScreenOrientation {
  angle?: number;
  type?: string;
  lock?: (orientation: OrientationLockType) => Promise<void>;
  unlock?: () => void;
}

export function useScreenLockOrientation(
  orientation: OrientationLockType = 'landscape',
): void {
  // When force-vertical is on, we render a CSS-rotated landscape player inside
  // a portrait viewport. Locking the OS to landscape in that mode would undo
  // the rotation; lock to portrait instead.
  const effectiveOrientation: OrientationLockType =
    config.FORCE_PORTRAIT_ORIENTATION && orientation === 'landscape'
      ? 'portrait'
      : orientation;

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const tryLock = () => {
      const screenOrientation = window.screen?.orientation as
        | LockableScreenOrientation
        | undefined;
      if (!document.fullscreenElement || !screenOrientation?.lock) return;
      screenOrientation
        .lock(effectiveOrientation)
        .catch(() => {
          // Expected on iOS Safari (NotSupportedError) and outside fullscreen
          // (SecurityError). The CSS @media fallback handles those cases.
        });
    };

    tryLock();
    document.addEventListener('fullscreenchange', tryLock);
    return () => {
      document.removeEventListener('fullscreenchange', tryLock);
    };
  }, [effectiveOrientation]);
}
